import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import CountUp from "react-countup";
import commentApi from "api/commentApi";
import {
  COLOR_CONTENT_AI_APPROVAL_STATUS,
  COLOR_CONTENT_COMMENT_STATUS,
  COLOR_LIST_TYPE_COMMENT,
  LIST_CONTENT_AI_APPROVAL_STATUS_OPTION,
  LIST_CONTENT_COMMENT_STATUS_OPTION,
  LIST_TYPE_COMMENT_OPTION,
} from "helpers/constans";
import { formatDateStringToKorea } from "helpers/format";
import { CONFIG_OPTION_TOAST_ERROR, CONFIG_OPTION_TOAST_NORMAL } from "common/toast";
import CopyWrapper from "components/Common/CopyWrapper";
import CollapseContent from "components/Common/CollapseContent";
import ModalPreview from "components/Common/ModalPreview";
import ModalMultiPreview from "components/Common/ModalMultiPreview";
import UpdateDropdownStatus from "components/Common/UpdateDropdownStatus";
import CommentPostForm from "./CommentPostForm";
import { Link } from "react-router-dom";
import TooltipCustom from "components/Common/TooltipCustom";
import ModalConfirm from "components/Common/ModalConfirm";

registerLocale("en", en);
registerLocale("ko", ko);

interface CommentRecord {
  id: number;
  status: string;
  approvalStatus: string;
  comment: string;
  user: {
    type: string;
    profile?: { imageUrls?: string[]; nickname?: string; fullname?: string; email?: string };
    store?: { thumbnailUrl?: string; managerName?: string; managerEmail?: string };
    email?: string;
    providerId?: string;
  };
  referralCode: string;
  source: string;
  type: string;
  createdAt: string;
  replies?: boolean;
  level?: number;
  hasMoreReplies?: boolean;
  currentPage?: number;
  children?: CommentRecord[];
  parentCommentId?: number;
  recordParent?: any;
  post?: any;
  position?: number;
}

type ParentCommentInfo = { id: number; comment: string };

interface Props {
  item?: any;
  triggerRefreshCommunity?: () => void;
}

const SORT_BY_DEFAULT = "createdAt";
const ORDER_BY_DEFAULT = "desc";

const CommentListByPostId: React.FC<Props> = ({ item, triggerRefreshCommunity }) => {
  const { t, i18n } = useTranslation();

  const [query, setQuery] = useState({
    page: 1,
    limit: 30,
    sortBy: SORT_BY_DEFAULT,
    orderBy: ORDER_BY_DEFAULT,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpenComment, setIsOpenComment] = useState<boolean>(false);
  const [itemCommentEdit, setItemCommentEdit] = useState<any>(null);
  const [itemCommentDelete, setItemCommentDelete] = useState<any>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState<boolean>(false);
  const [comments, setComments] = useState<CommentRecord[]>([]);
  const [parentCommentSelected, setParentCommentSelected] = useState<ParentCommentInfo | null>(null);
  const [pagination, setPagination] = useState<any>(null);
  const [itemsByCommentId, setItemsByCommentId] = useState<Record<number, { content: string; items: CommentRecord[] }>>({});
  const [toggleByCommentId, setToggleCommentId] = useState<Record<number, boolean>>({});
  const [linkImage, setLinkImage] = useState<any>(null);
  const postId = item?.id ?? null;

  const totalRecords = pagination?.total ?? comments.length ?? 0;

  const loadComments = useCallback(async () => {
    if (!postId) {
      setComments([]);
      setPagination(null);
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        page: query.page,
        limit: query.limit,
        sortBy: query.sortBy,
        orderBy: query.orderBy,
        postId,
        approvalStatus: "APPROVED",
      };

      const [commentResponse]: any = await Promise.all([
        commentApi.getComments(params),
      ]);

      if (commentResponse?.code === 200) {
        setComments(commentResponse?.data?.items || []);
        setPagination(commentResponse?.data?.pagination || null);
      } else if (commentResponse) {
        toast(commentResponse?.message || t("Failed to load comments."), CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(error?.message || String(error), CONFIG_OPTION_TOAST_ERROR);
    } finally {
      setIsLoading(false);
    }
  }, [postId, query.limit, query.orderBy, query.page, query.sortBy, t]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  useEffect(() => {
    setQuery((prev) => ({
      ...prev,
      page: 1,
    }));
    setItemsByCommentId({});
    setToggleCommentId({});
    setParentCommentSelected(null);
    setItemCommentEdit(null);
    setItemCommentDelete(null);
  }, [postId]);

  const fetchReplies = useCallback(async (comment: CommentRecord) => {
    const { id: commentId, comment: commentContent } = comment;
    const res: any = await commentApi.getReplyComments({
      parentCommentId: commentId,
      page: 1,
      limit: 99999999,
    });

    setItemsByCommentId((oldItemByCommentId) => ({
      ...oldItemByCommentId,
      [commentId]: {
        content: commentContent,
        items: res?.data?.items || [],
      },
    }));
  }, []);

  const handleOnConfirmUpdate = useCallback(async (payload: { id: number; status: string }) => {
    try {
      const response: any = await commentApi.putComment(payload?.id, { status: payload?.status });
      if (response?.code === 200) {
        const newComment = response.data;
        setComments((oldDatas) =>
          oldDatas.map((oldData) =>
            oldData.id === newComment.id
              ? {
                  ...oldData,
                  status: payload.status,
                }
              : oldData
          )
        );

        if (newComment.parentCommentId) {
          setItemsByCommentId((prev) => {
            if (!prev[newComment.parentCommentId]) {
              return prev;
            }
            return {
              ...prev,
              [newComment.parentCommentId]: {
                ...prev[newComment.parentCommentId],
                items: prev[newComment.parentCommentId].items.map((oldData) =>
                  oldData.id === newComment.id
                    ? {
                        ...oldData,
                        status: payload.status,
                      }
                    : oldData
                ),
              },
            };
          });
        }

        toast(`${t("The process has been completed.")}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
    }
  }, [t]);

  const handleUpdateApprovalOnGrid = useCallback(async (id: number, value: string) => {
    try {
      const response: any = await commentApi.updateApprovalStatusComment(id, { approvalStatus: value });
      if (response?.data) {
        const newComment = response?.data;
        setComments((oldDatas) =>
          oldDatas.map((oldData) =>
            oldData.id === newComment.id
              ? {
                  ...oldData,
                  approvalStatus: value,
                }
              : oldData
          )
        );

        if (newComment.parentCommentId) {
          setItemsByCommentId((prev) => {
            if (!prev[newComment.parentCommentId]) {
              return prev;
            }
            return {
              ...prev,
              [newComment.parentCommentId]: {
                ...prev[newComment.parentCommentId],
                items: prev[newComment.parentCommentId].items.map((oldData) =>
                  oldData.id === newComment.id
                    ? {
                        ...oldData,
                        approvalStatus: value,
                      }
                    : oldData
                ),
              },
            };
          });
        }
        toast(`${t("The process has been completed.")}`, CONFIG_OPTION_TOAST_NORMAL);
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
    }
  }, [t]);

  const handleExpand = useCallback(async (comment: CommentRecord) => {
    const { id: commentId } = comment;
    if (!comment.replies) {
      return;
    }
    if (!toggleByCommentId[commentId] && !itemsByCommentId[commentId]) {
      await fetchReplies(comment);
    }
    setToggleCommentId((oldToggleByCommentId) => ({
      ...oldToggleByCommentId,
      [commentId]: !oldToggleByCommentId[commentId],
    }));
  }, [fetchReplies, itemsByCommentId, toggleByCommentId]);

  const formattedData = useMemo(() => {
    const flatten: Array<CommentRecord & { level: number }> = [];
    const baseTotal = totalRecords;

    const loadChildItems = (comment: CommentRecord, level: number) => {
      flatten.push({
        ...comment,
        level,
      });

      if (comment.replies && itemsByCommentId[comment.id]?.items?.length > 0 && !!toggleByCommentId[comment.id]) {
        for (const childComment of itemsByCommentId[comment.id].items) {
          loadChildItems(childComment, level + 1);
        }
      }
    };

    for (let i = 0; i < comments.length; i++) {
      const comment = comments[i];
      const position = baseTotal - ((query.page - 1) * query.limit + i);
      const level = 0;
      flatten.push({
        ...comment,
        position,
        level,
      });

      if (comment.replies && itemsByCommentId[comment.id]?.items?.length > 0 && !!toggleByCommentId[comment.id]) {
        for (const childComment of itemsByCommentId[comment.id].items) {
          loadChildItems(childComment, level + 1);
        }
      }
    }

    return flatten;
  }, [comments, itemsByCommentId, toggleByCommentId, totalRecords, query.limit, query.page]);

  const findCommentById = useCallback(
    (commentId?: number | null) => {
      if (!commentId) {
        return null;
      }

      const topLevel = comments.find((comment) => comment.id === commentId);
      if (topLevel) {
        return { id: topLevel.id, comment: topLevel.comment ?? "" };
      }

      for (const value of Object.values(itemsByCommentId)) {
        const child = value?.items?.find((comment) => comment.id === commentId);
        if (child) {
          return { id: child.id, comment: child.comment ?? "" };
        }
      }

      return null;
    },
    [comments, itemsByCommentId]
  );

  const handleReply = useCallback((comment: CommentRecord) => {
    setParentCommentSelected({ id: comment.id, comment: comment.comment ?? "" });
    setItemCommentEdit(null);
    setIsOpenComment(true);
  }, []);

  const handleEditComment = useCallback(
    (comment: CommentRecord) => {
      const parentInfo =
        findCommentById(comment.parentCommentId) ??
        (comment.recordParent && comment.recordParent.id
          ? { id: comment.recordParent.id, comment: comment.recordParent.comment ?? "" }
          : null);
      setParentCommentSelected(parentInfo ?? null);
      setItemCommentEdit(comment);
      setIsOpenComment(true);
    },
    [findCommentById]
  );

  const handleRequestDelete = useCallback((comment: CommentRecord) => {
    setItemCommentDelete(comment);
  }, []);

  const handleDeleteComment = useCallback(async () => {
    const commentToDelete = itemCommentDelete;
    if (!commentToDelete?.id) {
      return;
    }

    try {
      setIsDeleteLoading(true);
      const response: any = await commentApi.deleteComment(commentToDelete.id);
      if (response?.code === 200 || response?.status === 200 || response?.data) {
        toast(`${t("The comment has been deleted.")}`, CONFIG_OPTION_TOAST_NORMAL);
        setItemCommentDelete(null);
        await loadComments();

        const parentId = commentToDelete.parentCommentId;
        if (parentId) {
          try {
            const res: any = await commentApi.getReplyComments({
              parentCommentId: parentId,
              page: 1,
              limit: 99999999,
            });
            setItemsByCommentId((prev) => ({
              ...prev,
              [parentId]: {
                content:
                  findCommentById(parentId)?.comment ?? prev[parentId]?.content ?? "",
                items: res?.data?.items || [],
              },
            }));
          } catch (error) {
            console.error(error);
          }
        } else {
          setItemsByCommentId((prev) => {
            const clone = { ...prev };
            delete clone[commentToDelete.id];
            return clone;
          });
          setToggleCommentId((prev) => {
            const clone = { ...prev };
            delete clone[commentToDelete.id];
            return clone;
          });
        }
      } else {
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
    } finally {
      setIsDeleteLoading(false);
    }
  }, [itemCommentDelete, loadComments, t, findCommentById]);

  const columns = useMemo(() => {
    return [
      {
        Header: t("No."),
        thWidth: 60,
        thClass: "whitespace-nowrap",
        Cell: ({ row }: any) => {
          return <div style={{ width: 35 }}>{row.original?.position}</div>;
        },
      },
      {
        Header: t("Status"),
        accessor: "status",
        thWidth: 100,
        thClass: "text-center",
        filterable: false,
        sortable: false,
        Cell: ({ value, row }: any) => {
          const record = row.original as CommentRecord;
          if (value === "DELETED") {
            return (
              <span className={`rounded-pill badge bg-${COLOR_CONTENT_COMMENT_STATUS[value] || "secondary"}`}>
                {t(LIST_CONTENT_COMMENT_STATUS_OPTION?.find((i) => i?.value === value)?.label || "")}
              </span>
            );
          }
          return (
            <div className="form-check form-switch form-switch-sm text-center" dir="ltr" style={{ minWidth: "80px" }}>
              <input
                type="checkbox"
                className="form-check-input"
                name="status"
                checked={value === "ACTIVE"}
                onChange={(e) => {
                  const checked = e.target.checked;
                  handleOnConfirmUpdate({
                    id: record.id,
                    status: checked ? "ACTIVE" : "INACTIVE",
                  });
                }}
              />
            </div>
          );
        },
      },
      {
        Header: t("Approved Status"),
        accessor: "approvalStatus",
        thWidth: 150,
        thClass: "text-center whitespace-nowrap",
        Cell: ({ value, row }: any) => {
          const record = row.original as CommentRecord;
          const sourceComment = record.source;
          return (
            <div className="text-center" style={{ minWidth: 100 }}>
              <div className="position-relative d-flex align-items-center gap-1 justify-content-center">
                {sourceComment === "ai" ? (
                  <UpdateDropdownStatus
                    item={record}
                    valueDefault={value}
                    handleUpdateStatus={handleUpdateApprovalOnGrid}
                    optionsStatus={LIST_CONTENT_AI_APPROVAL_STATUS_OPTION}
                    colorStatus={COLOR_CONTENT_AI_APPROVAL_STATUS}
                  />
                ) : (
                  <span className={`rounded-pill badge bg-${COLOR_CONTENT_AI_APPROVAL_STATUS[value] || "secondary"}`}>
                    {t(LIST_CONTENT_AI_APPROVAL_STATUS_OPTION?.find((item) => item?.value === value)?.label || "")}
                  </span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        Header: t("Comment Level"),
        accessor: "CommentLevel",
        thWidth: 140,
        thClass: "text-center whitespace-nowrap",
        Cell: ({ row }: any) => {
          const level = row.original?.level;
          return (
            <div className="text-center" style={{ minWidth: 80 }}>
              <span className={`rounded-pill badge bg-${level === 0 ? "primary" : "warning"}`}>
                {level === 0 ? t("Level Comment") : t("Level Reply Comment")}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Comment Content"),
        accessor: "comment",
        thWidth: 400,
        Cell: ({ row }: any) => {
          const record = row.original as CommentRecord;
          const comment = record.comment;
          const isExpanded = !!toggleByCommentId[record.id];
          const level = record.level || 0;
          const marginLeft = level * 20;

          return (
            comment && (
              <div className="d-flex flex-column" style={{ paddingLeft: marginLeft, maxWidth: 400 }}>
                <div className="d-flex flex-row align-items-center">
                  {record.replies === true && (
                    <span
                      onClick={() => handleExpand(record)}
                      style={{ cursor: "pointer", marginRight: 6, fontSize: 15, userSelect: "none" }}
                    >
                      {isExpanded ? "▼" : "▶"}
                    </span>
                  )}
                  <CopyWrapper contentCopy={comment} style={{ minWidth: "230px", whiteSpace: "pre-line" }}>
                    <CollapseContent
                      style={{ maxWidth: "230px" }}
                      dataOriginal={comment}
                      isRank={false}
                      isInlineBlock={true}
                      isShowModal={true}
                      titleModal={t("Content")}
                    />
                  </CopyWrapper>
                </div>
                {record?.parentCommentId && (
                  <small className="text-secondary">
                    <i className="ri-reply-fill"></i>{" "}
                    {`${t("Replying to")} : "${itemsByCommentId[record.parentCommentId]?.content}"`}
                  </small>
                )}
              </div>
            )
          );
        },
      },
      {
        Header: t("Nickname"),
        accessor: "user",
        thWidth: 280,
        Cell: ({ value: user, row }: any) => {
          const nickNameCreate = row?.original?.nickname;
          
          return (
            <div className="cursor-pointer" style={{ width: "180px" }}>
              <CopyWrapper contentCopy={nickNameCreate}>
                {`${nickNameCreate ? nickNameCreate : "-"}`}
              </CopyWrapper>
            </div>
          )
        },
      },
      {
        Header: t("Profile Nickname"),
        accessor: "profile",
        thWidth: 280,
        Cell: ({ row }: any) => {
          const user = row?.original?.user;
          const userType = user?.type;
          let nickname = user?.profile?.nickname || user?.profile?.fullname || "";
          if (userType === "MANAGER") {
            nickname = user?.store?.managerName || "";
          }

          return (
            <div className="cursor-pointer" style={{ width: "180px" }}>
              <CopyWrapper contentCopy={nickname}>
                {`${nickname || "-"}`}
              </CopyWrapper>
            </div>
          )
        },
      },
      {
        Header: t("Type Comment"),
        accessor: "type",
        thWidth: 150,
        thClass: "text-center whitespace-nowrap",
        Cell: ({ value }: any) => (
          <div className="text-center" style={{ minWidth: 80 }}>
            <span className={`rounded-pill badge bg-${COLOR_LIST_TYPE_COMMENT[value] || "secondary"}`}>
              {t(LIST_TYPE_COMMENT_OPTION?.find((item) => item?.value === value)?.label || "")}
            </span>
          </div>
        ),
      },
      {
        Header: t("Comment Date"),
        accessor: "createdAt",
        thWidth: 150,
        thClass: "text-end",
        sortable: true,
        Cell: ({ value }: any) => {
          const date = formatDateStringToKorea(value);
          const arrDate = String(date || "").split(" ");
          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              {value ? (
                <>
                  <span>{arrDate?.[0]}</span>
                  <br />
                  <span className="text-secondary">{arrDate?.[1]}</span>
                </>
              ) : (
                "-"
              )}
            </div>
          );
        },
      },
      {
        Header: t("Action"),
        thWidth: 180,
        thClass: "text-center",
        Cell: ({ row }: any) => {
          const record = row.original as CommentRecord;
          return (
            <ul
              className="list-inline flex-wrap justify-content-center hstack gap-2 mb-0"
              style={{ minWidth: "120px" }}
            >
              <TooltipCustom
                title={t("Reply Comment")}
                id={`reply-${record.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    handleReply(record);
                  }}
                >
                  <i className="ri-reply-line"></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Edit Comment")}
                id={`edit-${record.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-primary edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    handleEditComment(record);
                  }}
                >
                  <i className="ri-pencil-fill align-bottom"></i>
                </Link>
              </TooltipCustom>

              <TooltipCustom
                title={t("Delete Comment")}
                id={`delete-${record.id}`}
              >
                <Link
                  className="btn btn-sm btn-soft-danger edit-item-btn"
                  to="#"
                  onClick={(e: any) => {
                    e.preventDefault();
                    handleRequestDelete(record);
                  }}
                >
                  <i className="ri-delete-bin-3-fill align-bottom"></i>
                </Link>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ];
  }, [t, itemsByCommentId, toggleByCommentId, handleOnConfirmUpdate, handleExpand, handleUpdateApprovalOnGrid, handleReply, handleEditComment, handleRequestDelete]);

  const handleChangePage = useCallback((page: number) => {
    setQuery((prev) => ({
      ...prev,
      page: page + 1,
    }));
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((prev) => ({
      ...prev,
      ...sortBy,
    }));
  }, []);

  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  const onCloseCommentForm = () => {
    setIsOpenComment(false);
    setParentCommentSelected(null);
    setItemCommentEdit(null);
  };

  const triggerRefresh = async () => {
    const parentInfo = parentCommentSelected;
    const parentId = parentInfo?.id ?? itemCommentEdit?.parentCommentId ?? null;

    await loadComments();

    if (parentId) {
      try {
        const res: any = await commentApi.getReplyComments({
          parentCommentId: parentId,
          page: 1,
          limit: 99999999,
        });
        setItemsByCommentId((prev) => ({
          ...prev,
          [parentId]: {
            content: parentInfo?.comment ?? prev[parentId]?.content ?? "",
            items: res?.data?.items || [],
          },
        }));
        setToggleCommentId((prev) => ({
          ...prev,
          [parentId]: true,
        }));
      } catch (error) {
        console.error(error);
      }
    }

    onCloseCommentForm();
    // triggerRefreshCommunity && triggerRefreshCommunity();
  };

  useEffect(() => {
    document.title = `${t("Comment Detail")}| OnnyJob`;
    document.body.classList.remove("vertical-sidebar-enable");
  }, [i18n?.language, t]);

  return (
    <React.Fragment>
      <div>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <div id="customerList" style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}>
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp start={0} end={totalRecords || 0} duration={1} className="text-primary" />
                        </h5>
                      </div>
                    </div>
                    <div className="col-7 con-sm-6 text-end">
                      <div className="col-sm-auto">
                            <div>
                              <button
                                type="button"
                                className="btn btn-success add-btn"
                                onClick={() => {
                                  setParentCommentSelected(null);
                                  setItemCommentEdit(null);
                                  setIsOpenComment(true);
                                }}
                              >
                                <i className="ri-add-line align-bottom me-1"></i>
                                {t("Create Comment")}
                              </button>
                            </div>
                          </div>
                    </div>
                  </Row>
                </CardHeader>

                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={formattedData}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={totalRecords}
                      customPageCount={
                        query.limit ? Math.ceil(Number(totalRecords || 0) / Number(query.limit)) : undefined
                      }
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sortBy: query.sortBy,
                        orderBy: query.orderBy,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isLoading}
                      defaultScrollBottom={(formattedData ?? [])?.length < 5}
                    />
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </div>
            </Col>
          </Row>
        </Container>

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        {isOpenComment && (
          <CommentPostForm
            id={itemCommentEdit?.id}
            postId={postId}
            defaultValues={itemCommentEdit ?? undefined}
            setLinkImage={setLinkImage}
            triggerRefresh={triggerRefresh}
            onCloseCommentForm={onCloseCommentForm}
            parentComment={
              parentCommentSelected
                ? { id: parentCommentSelected.id, comment: parentCommentSelected.comment }
                : undefined
            }
          />
        )}

        {itemCommentDelete && (
          <ModalConfirm
            header={t("Delete This Comment")}
            title={t("Are you sure you want to delete this comment?")}
            content={<span className="d-block text-break">{itemCommentDelete?.comment || ""}</span>}
            isOpen={!!itemCommentDelete}
            isLoading={isDeleteLoading}
            onClose={() => setItemCommentDelete(null)}
            onConfirm={handleDeleteComment}
          />
        )}
      </div>
    </React.Fragment>
  );
};

export default CommentListByPostId;
